import { v4 as uuidv4 } from 'uuid';
// ==================== CONSTANTS ====================
export var HIGHLIGHT_COLOR = '#FFDA70';
export var SPEED_TIME = [1.5, 1, 0.5];
export var TYPES_OF_REVEALING = [
    'TOP_RIGHT (row-major)',
    'TOP_BOTTOM (column-major)',
    'BOTTOM_RIGHT',
    'BOTTOM_TOP',
];
export var COLOR_INDEX_MAP = {
    RED: 0,
    YELLOW: 1,
    BLUE: 2,
    BROWN: 3,
    ORANGE: 4,
    GREEN: 5,
    PURPLE: 6,
    WHITE: 7,
    GRAY: 8,
    BLACK: 9,
};
export var INDEX_COLOR_MAP = Object.entries(COLOR_INDEX_MAP).reduce(function (acc, _a) {
    var color = _a[0], index = _a[1];
    acc[index] = color;
    return acc;
}, {});
export var CHART_CONFIGS = {
    0: { rows: 10, columns: 10, startNumerator: 1, countNumerator: 1 },
    1: { rows: 10, columns: 20, startNumerator: 1, countNumerator: 1 },
    2: { rows: 10, columns: 12, startNumerator: 1, countNumerator: 1 },
    3: { rows: 10, columns: 10, startNumerator: 10, countNumerator: 10 },
    4: { rows: 1, columns: 1, startNumerator: 1, countNumerator: 1 },
};
export var DISPLAY_VALUES = [
    "\\(10\\)",
    "\\(\\frac{10}{10}\\)",
    "\\(10\\frac{10}{10}\\)",
    "\\(10.00\\)",
];
export var MAX_INPUT_LENGTH = 5;
export var generateChartId = function () { return "numberChart_".concat(uuidv4().replace(/-/g, '')); };
export var DEFAULT_CHART_DATA = {
    id: generateChartId(),
    lock: { positioning: false, editing: false },
    chartType: 0,
    startPosition: 0,
    direction: 0,
    rows: 1,
    columns: 1,
    countDirection: 0,
    numberType: 0,
    showGridLines: true,
    startNumerator: 1,
    startDenominator: 1,
    countNumerator: 1,
    countDenominator: 1,
    maxDigits: 0,
    cells: [],
    center: { x: 0, y: 0 },
    dimensions: { width: 0, height: 0 },
    simplfyFractions: false,
    scale: 1,
    stampIndex: undefined,
    actionType: '',
    choralDirectionIndex: 0,
};
// ==================== UTILITY FUNCTIONS ====================
/**
 * Converts between color name and color index
 * @param input - Either a color index (number) or color name (string)
 * @returns Object with colorIndex and colorFolder
 */
export var getColorInfo = function (input) {
    var _a, _b;
    if (typeof input === 'number') {
        var color = (_a = INDEX_COLOR_MAP[input]) !== null && _a !== void 0 ? _a : 'RED';
        return {
            colorIndex: input,
            colorFolder: color.toLowerCase(),
        };
    }
    var upperClr = input.toUpperCase();
    var colorIndex = (_b = COLOR_INDEX_MAP[upperClr]) !== null && _b !== void 0 ? _b : 0;
    return {
        colorIndex: colorIndex,
        colorFolder: upperClr.toLowerCase(),
    };
};
/**
 * Maps visual row/col to actual data row/col based on direction
 * @param r - Visual row index
 * @param c - Visual column index
 * @param direction - Direction value (0-3)
 * @param rows - Total number of rows
 * @param columns - Total number of columns
 * @returns Mapped row and col indices
 */
export var mapRowColByDirection = function (r, c, direction, rows, columns) {
    switch (direction) {
        case 0:
            return { row: r, col: c };
        case 1:
            return {
                row: Math.floor((c * rows + r) / columns),
                col: (c * rows + r) % columns,
            };
        case 2:
            return { row: rows - 1 - r, col: c };
        case 3:
            var bottomUpIndex = c * rows + (rows - 1 - r);
            return {
                row: Math.floor(bottomUpIndex / columns),
                col: bottomUpIndex % columns,
            };
        default:
            return { row: r, col: c };
    }
};
/**
 * Maps a linear index to row/col based on reveal direction
 * @param index - Linear index
 * @param revealDirectionIndex - Reveal direction (0-3)
 * @param rows - Total number of rows
 * @param columns - Total number of columns
 * @returns Row and col indices
 */
export var mapRevealIndexToRowCol = function (index, revealDirectionIndex, rows, columns) {
    switch (revealDirectionIndex) {
        case 0:
            return {
                row: Math.floor(index / columns),
                col: index % columns,
            };
        case 1:
            return {
                row: index % rows,
                col: Math.floor(index / rows),
            };
        case 2:
            return {
                row: rows - 1 - Math.floor(index / columns),
                col: index % columns,
            };
        case 3:
            return {
                row: rows - 1 - (index % rows),
                col: Math.floor(index / rows),
            };
        default:
            return {
                row: Math.floor(index / columns),
                col: index % columns,
            };
    }
};
/**
 * Gets client coordinates from mouse or touch events
 * @param e - Mouse or touch event
 * @returns x and y coordinates
 */
export var getClientPoint = function (e) {
    if ('touches' in e && e.touches.length > 0) {
        return {
            x: e.touches[0].clientX,
            y: e.touches[0].clientY,
        };
    }
    if ('changedTouches' in e && e.changedTouches.length > 0) {
        return {
            x: e.changedTouches[0].clientX,
            y: e.changedTouches[0].clientY,
        };
    }
    return {
        x: e.clientX,
        y: e.clientY,
    };
};
/**
 * Compares cell values with target numerator/denominator
 * @param cell - The cell to compare
 * @param targetNumerator - Target numerator value
 * @param targetDenominator - Target denominator value
 * @param numberType - Number type (0: integers, 1: fractions, 2: mixed, 3: decimals)
 * @returns Whether the cell matches the target values
 */
export var compareCellValues = function (cell, targetNumerator, targetDenominator, numberType) {
    if (numberType === 0) {
        return cell.numerator === targetNumerator && cell.denominator === 1;
    }
    return cell.numerator === targetNumerator && cell.denominator === targetDenominator;
};
/**
 * Checks if any cell in the grid is hidden
 * @param cells - 2D array of cells
 * @returns Whether any cell is hidden
 */
export var isAnyCellHidden = function (cells) {
    return cells.some(function (row) { return row.some(function (cell) { return cell.visible === false; }); });
};
/**
 * Gets the number of decimal places in a number
 * @param num - The number to check
 * @returns Number of decimal places
 */
export var getDecimalPlaces = function (num) {
    var str = num.toString();
    if (str.includes('.')) {
        return str.split('.')[1].length;
    }
    return 0;
};
/**
 * Gets stamp element data for a cell
 * @param cellData - The cell data
 * @returns Array of stamp data objects
 */
export var getStampElements = function (cellData) {
    if (!cellData || !cellData.iconIndex || cellData.iconIndex.length === 0) {
        return [];
    }
    return cellData.iconIndex.map(function (stampIdx, i) {
        var _a, _b;
        var colorIdx = (_b = (_a = cellData.colorIndex) === null || _a === void 0 ? void 0 : _a[i]) !== null && _b !== void 0 ? _b : 0;
        var colorFolder = getColorInfo(colorIdx).colorFolder;
        return {
            url: "./assets/images/manipulative/numberChart/".concat(colorFolder, "/stamp").concat(stampIdx, ".svg"),
            isOpaque: stampIdx === 0,
        };
    });
};
